import type { AttributeDefinition, PersonaAttributes } from '../api/client'

/*
  A seat's disposition, as a shape.

  Seven axes, all 0 to 10, all measuring the same kind of thing: how this member argues.
  Radars deserve their bad reputation when the axes are unrelated quantities, because the
  area they enclose then means nothing. These are seven comparable scales and the job here
  is not reading a value off the chart, it is recognising a shape. Two seats side by side
  and you can see why they disagree before you have read a word, which is the product.

  Drawn in the seat's own ramp, which is assigned once at creation and never changes, so the
  chart needs no palette of its own and cannot collide with the meaning already carried by
  plum, mint and red elsewhere.
*/

const SIZE = 100
const C = SIZE / 2
const R = 38

/** Twelve o'clock, then clockwise. The order is the catalog's, so it is stable per seat. */
function point(i: number, count: number, value: number): [number, number] {
  const angle = (Math.PI * 2 * i) / count - Math.PI / 2
  const r = (R * Math.max(0, Math.min(10, value))) / 10
  return [C + r * Math.cos(angle), C + r * Math.sin(angle)]
}

function ring(count: number, fraction: number): string {
  return Array.from({ length: count }, (_, i) => {
    const [x, y] = point(i, count, fraction * 10)
    return `${x.toFixed(2)},${y.toFixed(2)}`
  }).join(' ')
}

/**
 * Which way a label leans, so the ones on the left push left and the ones on the right push
 * right. Centring all seven ran "Contrarianism" into "Change appetite" along the bottom of a
 * seven-axis chart, which is exactly where two labels are always closest together.
 */
function anchor(i: number, count: number): string {
  const cos = Math.cos((Math.PI * 2 * i) / count - Math.PI / 2)
  if (cos > 0.3) return 'translate(0, -50%)'
  if (cos < -0.3) return 'translate(-100%, -50%)'
  return 'translate(-50%, -50%)'
}

export function Fingerprint({
  attributes, definitions, size = 44, showAxes = false, web = true, title,
}: {
  attributes: PersonaAttributes
  /** The whole catalog. Disposition is filtered out of it, in its own order. */
  definitions: AttributeDefinition[]
  size?: number
  /** A dot per axis. Only worth the room at the large size. */
  showAxes?: boolean
  /** The rings and spokes. Off at thumbnail size, where they are clutter rather than scale. */
  web?: boolean
  title?: string
}) {
  const axes = definitions.filter(d => d.category === 'disposition')
  if (axes.length < 3) return null

  const values = axes.map(d => attributes.disposition[d.key] ?? 5)
  const shape = values
    .map((v, i) => point(i, axes.length, v))
    .map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ')

  return (
    <svg
      className={`fp${web ? '' : ' fp--bare'}`}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {/* Two rings, not five. The web is orientation rather than a scale to read off, and at
          46px it is neither: the outline alone carries the difference between two seats. */}
      {web && (
        <>
          <polygon className="fp__web" points={ring(axes.length, 1)} />
          <polygon className="fp__web" points={ring(axes.length, 0.5)} />
          {axes.map((_, i) => {
            const [x, y] = point(i, axes.length, 10)
            return <line key={i} className="fp__spoke" x1={C} y1={C} x2={x} y2={y} />
          })}
        </>
      )}

      <polygon className="fp__shape" points={shape} />

      {showAxes && values.map((v, i) => {
        const [x, y] = point(i, axes.length, v)
        return <circle key={i} className="fp__dot" cx={x} cy={y} r={1.8} />
      })}
    </svg>
  )
}

/**
 * The same shape with its axes named, for the editor, where there is room for them and
 * where somebody is deciding what to change.
 */
export function FingerprintLarge({
  attributes, definitions, size = 220,
}: {
  attributes: PersonaAttributes
  definitions: AttributeDefinition[]
  size?: number
}) {
  const axes = definitions.filter(d => d.category === 'disposition')
  if (axes.length < 3) return null

  return (
    <div className="fp-large" style={{ width: size, height: size }}>
      <Fingerprint
        attributes={attributes}
        definitions={definitions}
        size={size}
        showAxes
        title={`How this member argues, across ${axes.length} axes`}
      />

      {/*
        Placed on the same geometry as the chart rather than positioned by hand, so an eighth
        disposition axis moves the labels without anybody remembering to.
      */}
      {axes.map((d, i) => {
        const [x, y] = point(i, axes.length, 12.8)
        return (
          <span
            key={d.key}
            className="fp-large__label"
            style={{
              left: `${(x / SIZE) * 100}%`,
              top: `${(y / SIZE) * 100}%`,
              transform: anchor(i, axes.length),
            }}
          >
            {d.displayName}
          </span>
        )
      })}
    </div>
  )
}
